import { styled } from '@withjoy/joykit/theme';
import { animationTransition } from '@shared/utils/animationTransition';

/**
 * Container for the combined menu trigger zone (hamburger + Joy logo).
 * Hover anywhere in this zone triggers both the hamburger hover effect and particle animation.
 * `$columnGap` is a theme space-scale index so each consumer keeps its own spacing (the admin header
 * uses 4; the unified-shell nav uses 3 per Figma).
 */
export const StyledMenuTriggerZone = styled.div<{ $columnGap: number }>`
  display: flex;
  align-items: center;
  column-gap: ${({ theme, $columnGap }) => theme.space[$columnGap]};
  cursor: pointer;
`;

/**
 * Wrapper for the hamburger icon with hover/active states. The hover effect (gray background)
 * only appears around the hamburger, but is triggered when hovering anywhere in the parent
 * `StyledMenuTriggerZone`.
 */
export const StyledHamburgerWrapper = styled.div<{ $isHovered: boolean; $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space[1]};
  border-radius: ${({ theme }) => theme.radii[2]};
  color: inherit;
  background-color: ${({ $isHovered, $isActive, theme }) => ($isActive ? theme.colors.mono3 : $isHovered ? theme.colors.mono2 : 'transparent')};
  ${({ $isHovered, theme }) => $isHovered && `color: ${theme.colors.newUIBrandPrimary};`}
  transition: ${animationTransition('background-color', 'color')};
`;

/**
 * The admin header's sized Joy logo. The styled component itself lives in `@shared` so Service
 * Center's header can render the identical mark without importing `@apps/admin`; re-exported
 * under its original name because admin's call sites (`BurgerLogoTrigger`, the burger drawer's
 * `MenuList`) know it by that.
 */
export { SizedParticleJoyLogo as StyledParticleJoyLogo } from '@shared/components/ParticleJoyLogo/SizedParticleJoyLogo';
